import { create } from "zustand";
import { api, Domain, Organization, Source, Alert, RemediationTask, ActionItemsSummary } from "@/lib/api";

/**
 * Shared data store for commonly-fetched entities.
 * This store reduces redundant API calls by caching data that's used across multiple pages.
 *
 * Usage pattern:
 * 1. Pages should use `ensureDomains()` instead of directly calling `api.getDomains()`
 * 2. The store tracks staleness via timestamps and will refetch if data is stale
 * 3. Manual refresh is available via `refreshDomains()` etc.
 */

// How long before data is considered stale (in ms)
const STALE_TIME = 60 * 1000; // 1 minute

// Maximum time to wait for a loading operation (prevents infinite waits)
const MAX_WAIT_TIME = 30 * 1000; // 30 seconds

/**
 * Wait for a loading flag to become false using Zustand subscribe
 * This is more efficient than polling and handles race conditions properly
 */
function waitForLoadingComplete<T>(
  store: { subscribe: (listener: (state: T) => void) => () => void; getState: () => T },
  isLoadingSelector: (state: T) => boolean
): Promise<void> {
  return new Promise((resolve, reject) => {
    // If already not loading, resolve immediately
    if (!isLoadingSelector(store.getState())) {
      resolve();
      return;
    }

    // Set up a timeout to prevent infinite waits
    const timeoutId = setTimeout(() => {
      unsubscribe();
      reject(new Error("Timeout waiting for data to load"));
    }, MAX_WAIT_TIME);

    // Subscribe to state changes
    const unsubscribe = store.subscribe((state) => {
      if (!isLoadingSelector(state)) {
        clearTimeout(timeoutId);
        unsubscribe();
        resolve();
      }
    });
  });
}

interface DataState {
  // Domains
  domains: Domain[];
  domainsLoading: boolean;
  domainsLastFetch: number | null;
  domainsError: string | null;

  // Organization (already in auth store, but we expose a helper)
  organization: Organization | null;
  organizationLoading: boolean;
  organizationLastFetch: number | null;

  // Sources (all sources across domains)
  sources: Source[];
  sourcesLoading: boolean;
  sourcesLastFetch: number | null;
  sourcesTier: string;
  sourcesPermissions: {
    canViewHostname: boolean;
    canLabelSources: boolean;
    canViewRemediation: boolean;
    canApplyRemediation: boolean;
  };

  // Alerts
  alerts: Alert[];
  alertsLoading: boolean;
  alertsLastFetch: number | null;

  // Remediation Tasks
  remediationTasks: RemediationTask[];
  remediationTasksLoading: boolean;
  remediationTasksLastFetch: number | null;

  // Action Items Summary
  actionItemsSummary: ActionItemsSummary | null;
  actionItemsLoading: boolean;
  actionItemsLastFetch: number | null;

  // Actions - Ensure data is loaded (fetches if not present or stale)
  ensureDomains: () => Promise<Domain[]>;
  ensureOrganization: () => Promise<Organization | null>;
  ensureSources: () => Promise<Source[]>;
  ensureAlerts: () => Promise<Alert[]>;
  ensureRemediationTasks: () => Promise<RemediationTask[]>;
  ensureActionItemsSummary: (domainId?: string) => Promise<ActionItemsSummary | null>;

  // Actions - Force refresh (always fetches fresh data)
  refreshDomains: () => Promise<Domain[]>;
  refreshOrganization: () => Promise<Organization | null>;
  refreshSources: () => Promise<Source[]>;
  refreshAlerts: () => Promise<Alert[]>;
  refreshRemediationTasks: () => Promise<RemediationTask[]>;
  refreshActionItemsSummary: (domainId?: string) => Promise<ActionItemsSummary | null>;

  // Actions - Update individual items
  updateDomain: (domain: Domain) => void;
  updateSource: (source: Source) => void;
  updateAlert: (alert: Alert) => void;
  updateRemediationTask: (task: RemediationTask) => void;

  // Actions - Invalidate cache (marks data as stale)
  invalidateDomains: () => void;
  invalidateSources: () => void;
  invalidateAlerts: () => void;
  invalidateRemediationTasks: () => void;
  invalidateActionItemsSummary: () => void;
  invalidateAll: () => void;

  // Helper to check if data is stale
  isStale: (lastFetch: number | null) => boolean;
}

export const useDataStore = create<DataState>((set, get) => ({
  // Initial state
  domains: [],
  domainsLoading: false,
  domainsLastFetch: null,
  domainsError: null,

  organization: null,
  organizationLoading: false,
  organizationLastFetch: null,

  sources: [],
  sourcesLoading: false,
  sourcesLastFetch: null,
  sourcesTier: "free",
  sourcesPermissions: {
    canViewHostname: false,
    canLabelSources: false,
    canViewRemediation: false,
    canApplyRemediation: false,
  },

  alerts: [],
  alertsLoading: false,
  alertsLastFetch: null,

  remediationTasks: [],
  remediationTasksLoading: false,
  remediationTasksLastFetch: null,

  actionItemsSummary: null,
  actionItemsLoading: false,
  actionItemsLastFetch: null,

  // Check if data is stale
  isStale: (lastFetch: number | null) => {
    if (!lastFetch) return true;
    return Date.now() - lastFetch > STALE_TIME;
  },

  // Ensure domains are loaded
  ensureDomains: async () => {
    const state = get();

    // Return cached data if fresh
    if (!state.isStale(state.domainsLastFetch) && state.domains.length > 0) {
      return state.domains;
    }

    // If already loading, wait for it using Zustand subscribe (more efficient than polling)
    if (state.domainsLoading) {
      try {
        await waitForLoadingComplete(
          useDataStore,
          (s: DataState) => s.domainsLoading
        );
      } catch {
        // Timeout - return whatever we have
        console.warn("Timeout waiting for domains to load");
      }
      return get().domains;
    }

    return get().refreshDomains();
  },

  refreshDomains: async () => {
    set({ domainsLoading: true, domainsError: null });
    try {
      const { domains } = await api.getDomains();
      set({
        domains,
        domainsLoading: false,
        domainsLastFetch: Date.now(),
        domainsError: null,
      });
      return domains;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to fetch domains";
      set({ domainsLoading: false, domainsError: errorMessage });
      return get().domains;
    }
  },

  // Ensure organization is loaded
  ensureOrganization: async () => {
    const state = get();

    if (!state.isStale(state.organizationLastFetch) && state.organization) {
      return state.organization;
    }

    if (state.organizationLoading) {
      try {
        await waitForLoadingComplete(
          useDataStore,
          (s: DataState) => s.organizationLoading
        );
      } catch {
        console.warn("Timeout waiting for organization to load");
      }
      return get().organization;
    }

    return get().refreshOrganization();
  },

  refreshOrganization: async () => {
    set({ organizationLoading: true });
    try {
      const { organization } = await api.getOrganization();
      set({
        organization,
        organizationLoading: false,
        organizationLastFetch: Date.now(),
      });
      return organization;
    } catch (error) {
      set({ organizationLoading: false });
      console.error("Failed to fetch organization:", error);
      return get().organization;
    }
  },

  // Ensure sources are loaded
  ensureSources: async () => {
    const state = get();

    if (!state.isStale(state.sourcesLastFetch) && state.sources.length > 0) {
      return state.sources;
    }

    if (state.sourcesLoading) {
      try {
        await waitForLoadingComplete(
          useDataStore,
          (s: DataState) => s.sourcesLoading
        );
      } catch {
        console.warn("Timeout waiting for sources to load");
      }
      return get().sources;
    }

    return get().refreshSources();
  },

  refreshSources: async () => {
    set({ sourcesLoading: true });
    try {
      const response = await api.getAllSources();
      set({
        sources: response.sources,
        sourcesTier: response.tier,
        sourcesPermissions: response.permissions,
        sourcesLoading: false,
        sourcesLastFetch: Date.now(),
      });
      return response.sources;
    } catch (error) {
      set({ sourcesLoading: false });
      console.error("Failed to fetch sources:", error);
      return get().sources;
    }
  },

  // Ensure alerts are loaded
  ensureAlerts: async () => {
    const state = get();

    // isStale(null) is true, so a null lastFetch always falls through to refresh.
    // The prior `alerts.length >= 0` clause was always true; removed.
    if (!state.isStale(state.alertsLastFetch)) {
      return state.alerts;
    }

    if (state.alertsLoading) {
      try {
        await waitForLoadingComplete(
          useDataStore,
          (s: DataState) => s.alertsLoading
        );
      } catch {
        console.warn("Timeout waiting for alerts to load");
      }
      return get().alerts;
    }

    return get().refreshAlerts();
  },

  refreshAlerts: async () => {
    set({ alertsLoading: true });
    try {
      const { alerts } = await api.getAlerts();
      set({
        alerts,
        alertsLoading: false,
        alertsLastFetch: Date.now(),
      });
      return alerts;
    } catch (error) {
      set({ alertsLoading: false });
      console.error("Failed to fetch alerts:", error);
      return get().alerts;
    }
  },

  // Ensure remediation tasks are loaded
  ensureRemediationTasks: async () => {
    const state = get();

    if (!state.isStale(state.remediationTasksLastFetch)) {
      return state.remediationTasks;
    }

    if (state.remediationTasksLoading) {
      try {
        await waitForLoadingComplete(
          useDataStore,
          (s: DataState) => s.remediationTasksLoading
        );
      } catch {
        console.warn("Timeout waiting for remediation tasks to load");
      }
      return get().remediationTasks;
    }

    return get().refreshRemediationTasks();
  },

  refreshRemediationTasks: async () => {
    set({ remediationTasksLoading: true });
    try {
      const { tasks } = await api.getRemediationTasks();
      set({
        remediationTasks: tasks,
        remediationTasksLoading: false,
        remediationTasksLastFetch: Date.now(),
      });
      return tasks;
    } catch (error) {
      set({ remediationTasksLoading: false });
      console.error("Failed to fetch remediation tasks:", error);
      return get().remediationTasks;
    }
  },

  // Ensure action items summary is loaded
  ensureActionItemsSummary: async (domainId?: string) => {
    const state = get();

    if (!state.isStale(state.actionItemsLastFetch) && state.actionItemsSummary) {
      return state.actionItemsSummary;
    }

    if (state.actionItemsLoading) {
      try {
        await waitForLoadingComplete(
          useDataStore,
          (s: DataState) => s.actionItemsLoading
        );
      } catch {
        console.warn("Timeout waiting for action items summary to load");
      }
      return get().actionItemsSummary;
    }

    return get().refreshActionItemsSummary(domainId);
  },

  refreshActionItemsSummary: async (domainId?: string) => {
    set({ actionItemsLoading: true });
    try {
      const summary = await api.getActionItemsSummary(domainId);
      set({
        actionItemsSummary: summary,
        actionItemsLoading: false,
        actionItemsLastFetch: Date.now(),
      });
      return summary;
    } catch (error) {
      set({ actionItemsLoading: false });
      console.error("Failed to fetch action items summary:", error);
      return get().actionItemsSummary;
    }
  },

  // Update individual items
  updateDomain: (domain: Domain) => {
    set((state) => ({
      domains: state.domains.map((d) => (d.id === domain.id ? domain : d)),
    }));
  },

  updateSource: (source: Source) => {
    set((state) => ({
      sources: state.sources.map((s) => (s.id === source.id ? source : s)),
    }));
  },

  updateAlert: (alert: Alert) => {
    set((state) => ({
      alerts: state.alerts.map((a) => (a.id === alert.id ? alert : a)),
    }));
  },

  updateRemediationTask: (task: RemediationTask) => {
    set((state) => ({
      remediationTasks: state.remediationTasks.map((t) => (t.id === task.id ? task : t)),
    }));
  },

  // Invalidate cache
  invalidateDomains: () => set({ domainsLastFetch: null }),
  invalidateSources: () => set({ sourcesLastFetch: null }),
  invalidateAlerts: () => set({ alertsLastFetch: null }),
  invalidateRemediationTasks: () => set({ remediationTasksLastFetch: null }),
  invalidateActionItemsSummary: () => set({ actionItemsLastFetch: null }),
  invalidateAll: () =>
    set({
      domainsLastFetch: null,
      organizationLastFetch: null,
      sourcesLastFetch: null,
      alertsLastFetch: null,
      remediationTasksLastFetch: null,
      actionItemsLastFetch: null,
    }),
}));

/**
 * Hook for dashboard data - fetches all commonly-needed data in parallel
 * Returns loading state and data
 */
export function useDashboardData() {
  const store = useDataStore();

  const loadAll = async () => {
    // Fetch all data in parallel
    await Promise.all([
      store.ensureDomains(),
      store.ensureOrganization(),
      store.ensureSources(),
      store.ensureAlerts(),
      store.ensureRemediationTasks(),
    ]);
  };

  return {
    loadAll,
    domains: store.domains,
    organization: store.organization,
    sources: store.sources,
    alerts: store.alerts,
    remediationTasks: store.remediationTasks,
    isLoading:
      store.domainsLoading ||
      store.organizationLoading ||
      store.sourcesLoading ||
      store.alertsLoading ||
      store.remediationTasksLoading,
    sourcesTier: store.sourcesTier,
    sourcesPermissions: store.sourcesPermissions,
  };
}
